import * as React from "react";
import { useFormContext } from "react-hook-form";
import { Button, CheckBox, Divider, Text, Trans } from "../../components";
import { Tools } from "../../utils";
import { SVGs } from "../../assets";
import { useAuthStore } from "../../stores";
import { PaymentOrderItemPayload } from "../../apis/order";
import { format } from "date-fns";

type BookingPaymentModalBodyProps = {
  t: (key: string) => string;
  onClose: () => void;
  onProceed: (values: any) => void;
  summary: any;
};

export function BookingPaymentModalBody(props: BookingPaymentModalBodyProps) {
  const form = useFormContext();
  const [termsChecked, orderItems, selectedDate, selectedTime] = form.watch([
    "termsAccepted",
    "order.order_items",
    "serviceDate",
    "serviceTime",
  ]);
  const { user } = useAuthStore();
  const hasAppointment = !!selectedDate && !!selectedTime;

  const payableAmount = props.summary?.finalPrice ?? 0;
  const payableAmountLabel = Tools.formatEur(payableAmount);

  return (
    <div className="space-y-[30px]">
      <div className="rounded-[6px] bg-primary p-[14px]  flex gap-[10px] items-start">
        <img src={SVGs.infoRoundFilled} alt="info" className="mt-[3px]" />
        <Text>{props.t("booking.billableAmountNotice")}</Text>
      </div>
      {[
        ...orderItems.map((item: PaymentOrderItemPayload, index: number) => ({
          icon: SVGs.AvatarRound,
          label: `${props.t("booking.visitor")} ${orderItems.length > 1 ? `- ${index + 1}` : ""}`,
          title: item.visitorFirstName + " " + item.visitorLastName,
          subtitle:
            item.insuranceProviderName + " - " + item.insurancePolicyNumber,
        })),
        ...(hasAppointment
          ? [
              {
                icon: SVGs.CalendarScheduled,
                label: props.t("booking.appointment"),
                title: `${format(selectedDate, "MMM d")} ${props.t("booking.at")} ${selectedTime}`,
                subtitle: props.summary?.location?.name ?? "",
              },
            ]
          : []),
      ].map(({ icon: Icon, ..._ }, index) => (
        <React.Fragment key={index}>
          <div className="flex gap-[10px] items-center justify-between">
            <div className="flex gap-[8px] items-center">
              <Icon className="text-text-primary w-[18px] h-[18px]" />
              <Text>{_.label.toUpperCase()}</Text>
            </div>
            <div className="text-center space-y-[4px]">
              <Text level={2} className="font-normal">
                {_.title}
              </Text>
              <Text>{_.subtitle}</Text>
            </div>
          </div>
          <Divider />
        </React.Fragment>
      ))}
      <div className="text-center space-y-[5px]">
        <Text className="font-medium">
          {props.t(
            user?.paymentRequired ? "general.payableAmount" : "general.billableAmount",
          )}
        </Text>
        <Text level={2} className="font-normal">
          {payableAmountLabel}
        </Text>
      </div>
      <Text level={4} className="font-normal">
        {props.t("booking.confirmDetailsStatement")}
      </Text>
      <CheckBox
        value={termsChecked}
        label={<Trans i18nKey="booking.iAgreeTo" />}
        errorMessage={String(
          form.formState.errors?.termsAccepted?.message ?? "",
        )}
        onChange={(checked) => {
          form.setValue("termsAccepted", checked);
          if (checked) form.clearErrors("termsAccepted");
        }}
      />
      <div className="flex gap-[16px]">
        <Button
          className="w-full"
          color="secondary"
          onClick={() => props.onClose()}
        >
          {props.t("booking.editDetails")}
        </Button>
        <Button
          className="w-full"
          color="secondaryDark"
          disabled={!termsChecked}
          onClick={form.handleSubmit(async (values) => {
            await props.onProceed(values);
            props.onClose();
          })}
        >
          {user?.paymentRequired
            ? props.t("booking.iUnderstand")
            : props.t("booking.confirmBooking")}
        </Button>
      </div>
    </div>
  );
}
